import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import i18next from 'i18next';
import { all, put, call, takeEvery } from 'redux-saga/effects';
import get from 'lodash/get';
import { Url, URLS, USER_PROFILE_POSTS_SUB_PATHS } from '@wix/da-url';
import ddt from '@wix/da-ddt';
import { getDeviationUrl } from '../../DeviationViews/_helpers';
import { convertToServerXhrPayload } from '../../DaEditorWrapper/utils';
import { requestNapi, requestPuppy } from '@wix/da-http-client/pkg/request';
import { normalize } from '../../redux/normalizr';
import { putEntities } from '../../redux/entities/actions';
import { deviationSchema } from '../../redux/normalizr/schemas/deviation';
import { editDeviationSuccess } from '../../redux/deviations/actionCreators';
import { logBiEvent } from '../../biLogger/redux/saga';
import { ResourceType } from '../../types/resource';
import { addNotification } from '../../Notifications/redux/actionCreators';
import { NotificationType } from '../../Notifications';
import { WriterPopupType } from './types';
import { publishWriterDeviation, saveWriterDeviation, saveWriterDeviationSuccess, saveWriterDeviationFailure, setIsRedirecting } from './actions';
import { convertResponseErrorToWriterError, fakeResponse, getWriterEndpoint } from './utils';
import { PureBiData } from '@wix/da-bi/pkg/events';
const logger = ddt.createLogger('writer.saga');
function* dispatchSaveWriterDeviationFailure(response) {
    yield put(saveWriterDeviationFailure(convertResponseErrorToWriterError(response === null || response === void 0 ? void 0 : response.errorDetails)));
}
function* doCreateWriterDeviation(type, groupId, localState) {
    const { journalTitle: title = '', editorState, coverImage } = localState;
    logger.log('doCreateWriterDeviation', {
        type,
        groupId,
        localState
    });
    return yield call(requestNapi, {
        method: 'post',
        url: getWriterEndpoint('/create', type),
        data: _object_spread({
            title,
            group_id: groupId,
            cover_image_deviation_id: coverImage === null || coverImage === void 0 ? void 0 : coverImage.deviationId
        }, convertToServerXhrPayload(editorState === null || editorState === void 0 ? void 0 : editorState.rawContentState, editorState === null || editorState === void 0 ? void 0 : editorState.editorContent, editorState === null || editorState === void 0 ? void 0 : editorState.features))
    }, fakeResponse);
}
function* doUpdateWriterDeviation(type, groupId, deviation, localState) {
    var _localState_visibility;
    const { journalTitle: title = '', editorState, coverImage } = localState;
    logger.log('doUpdateWriterDeviation', {
        type,
        groupId,
        deviation,
        localState
    });
    const result = yield call(requestNapi, {
        method: 'post',
        url: getWriterEndpoint('/update/content', type),
        data: _object_spread({
            title,
            group_id: groupId,
            deviationid: deviation === null || deviation === void 0 ? void 0 : deviation.deviationId,
            cover_image_deviation_id: coverImage === null || coverImage === void 0 ? void 0 : coverImage.deviationId
        }, convertToServerXhrPayload(editorState === null || editorState === void 0 ? void 0 : editorState.rawContentState, editorState === null || editorState === void 0 ? void 0 : editorState.editorContent, editorState === null || editorState === void 0 ? void 0 : editorState.features))
    }, fakeResponse);
    if (!(result === null || result === void 0 ? void 0 : result.deviation)) {
        logger.log('failure', result);
        return result;
    }
    if (type === WriterPopupType.LITERATURE) {
        return result;
    }
    return yield call(requestPuppy, {
        method: 'post',
        url: getWriterEndpoint('/update/metadata', type),
        data: {
            deviationid: deviation === null || deviation === void 0 ? void 0 : deviation.deviationId,
            group_id: groupId,
            featured: localState.isFeaturedPost,
            // TODO: Uncomment this and replace it with the real mature param
            // mature: localState.isMaturePost,
            tiers: ((_localState_visibility = localState.visibility) === null || _localState_visibility === void 0 ? void 0 : _localState_visibility.type) === 'tier' ? localState.visibility.tiers.map((tier)=>tier.deviationId) : undefined
        }
    }, fakeResponse);
}
/** Sends a request to our create or update endpoint depending on if the provided deviation exists */ function* doCreateOrUpdateDeviation(type, groupId, deviation, localState) {
    let result = null;
    if ((deviation === null || deviation === void 0 ? void 0 : deviation.deviationId) === undefined) {
        result = yield call(doCreateWriterDeviation, type, groupId, localState);
    } else {
        result = yield call(doUpdateWriterDeviation, type, groupId, deviation, localState);
    }
    if (result && (result === null || result === void 0 ? void 0 : result.deviation)) {
        yield call(handleSuccess, {
            deviation: result === null || result === void 0 ? void 0 : result.deviation,
            type,
            groupId
        }, (deviation === null || deviation === void 0 ? void 0 : deviation.deviationId) !== undefined);
    } else {
        yield call(dispatchSaveWriterDeviationFailure, result);
    }
    return result;
}
function* handleSuccess({ deviation, type, groupId }, wasEdit = false) {
    const { result: deviationId, entities } = normalize(deviation, deviationSchema);
    const resultActions = [
        put(putEntities({
            entities
        })),
        // Even though deviationId is expected to be a number and we're passing through the normalized
        // deviation id (which could be number | string), it should only ever receive
        // a deviation that production an id of type number
        put(saveWriterDeviationSuccess({
            deviationId,
            type
        }))
    ];
    if (wasEdit) {
        resultActions.push(put(editDeviationSuccess({
            deviation
        })));
    }
    if (groupId) {
        resultActions.push(put(addNotification(NotificationType.Generic, {
            message: i18next.t('writer.error.pendingApproval'),
            isError: false,
            cardProps: {
                multiLine: true,
                duration: 5000
            }
        })));
    }
    yield all(resultActions);
    logger.log('success', deviationId);
}
function* handleRedirect(url) {
    logger.log('redirect', url);
    window.location.assign(url);
}
function* handleSaveWriterDeviation(action) {
    // need to call "setIsRedirecting" before "doCommonSaveResultCleanup" to have a smooth
    // transition from isSubmitting to isRedirecting
    yield put(setIsRedirecting({
        isRedirecting: true
    }));
    const { type, deviation, groupId, localState } = action.payload;
    const result = yield call(doCreateOrUpdateDeviation, type, groupId, deviation, localState);
    if (result === null || result === void 0 ? void 0 : result.deviation) {
        var _result_deviation_author;
        // Upon successful submission, we should redirect to Posts/Drafts tab if unpublished,
        // browse posts feed if it is published
        const authorName = ((_result_deviation_author = result.deviation.author) === null || _result_deviation_author === void 0 ? void 0 : _result_deviation_author.username) || '';
        let redirectTo = result.deviation.isPublished ? getDeviationUrl(result.deviation) : Url.userPostsLink({
            username: authorName,
            subPath: USER_PROFILE_POSTS_SUB_PATHS.DRAFTS
        });
        if (type === WriterPopupType.LITERATURE) {
            // Saving a Literature draft should redirect to the sta.sh draft folder
            // but since we do not know the drafts folder hash (yet to be implemented
            // on the backend) we should redirect to the sta.sh root
            redirectTo = Url.buildFromPath(URLS.STASH);
            // If its published we need to redirect back to submit not stash
            if (deviation === null || deviation === void 0 ? void 0 : deviation.isPublished) {
                redirectTo = Url.submissionLinkWithId(deviation.deviationId, true);
            }
        }
        yield call(handleRedirect, redirectTo);
    } else {
        yield put(setIsRedirecting({
            isRedirecting: false
        }));
    }
}
function* handlePublishWriterDeviation(action) {
    var _localState_visibility;
    const { type, groupId, refreshAfterSubmit, deviation, localState } = action.payload;
    logger.log('publishWriterDeviation', action.payload);
    // need to call "setIsRedirecting" before any save/publish requests to have a smooth
    // transition from isSubmitting to isRedirecting
    yield put(setIsRedirecting({
        isRedirecting: true
    }));
    const saveResult = yield call(doCreateOrUpdateDeviation, type, groupId, deviation, localState);
    if (!saveResult || !saveResult.deviation) {
        if (saveResult && saveResult.errorCode === 302) {
            window.location.replace(saveResult.errorDetails.redirectUrl);
        }
        yield call(dispatchSaveWriterDeviationFailure, saveResult);
        return;
    }
    const deviationid = get(saveResult.deviation, 'deviationId', null);
    if (type === WriterPopupType.LITERATURE) {
        // Literature submission should finalize on the submit page
        if (!(deviation === null || deviation === void 0 ? void 0 : deviation.isPublished)) {
            yield call(logBiEvent, PureBiData({
                evid: 11,
                group_exclusive: groupId !== undefined,
                typeid: ResourceType.DEVIATION,
                itemid: deviationid
            }));
        }
        yield call(handleRedirect, `${Url.submissionLinkWithId(deviationid, true)}`);
        return;
    }
    const result = yield call(requestPuppy, {
        method: 'post',
        url: getWriterEndpoint('/publish', type),
        data: {
            deviationid,
            featured: localState.isFeaturedPost,
            // TODO: Uncomment this and replace it with the real mature param
            // mature: localState.isMaturePost,
            group_id: groupId,
            tiers: ((_localState_visibility = localState.visibility) === null || _localState_visibility === void 0 ? void 0 : _localState_visibility.type) === 'tier' ? localState.visibility.tiers.map((tier)=>tier.deviationId) : undefined
        }
    }, fakeResponse);
    logger.log('response', result);
    if (result && result.deviation) {
        const returnedDeviation = result.deviation;
        yield call(logBiEvent, PureBiData({
            evid: 11,
            group_exclusive: groupId !== undefined,
            typeid: ResourceType.JOURNAL,
            itemid: returnedDeviation.deviationId
        }));
        yield call(handleSuccess, {
            deviation: returnedDeviation,
            type,
            groupId
        }, false);
        // Upon successful submission, we should redirect
        if (refreshAfterSubmit) {
            window.location.reload();
        } else {
            yield call(handleRedirect, getDeviationUrl(returnedDeviation));
        }
    } else {
        yield put(setIsRedirecting({
            isRedirecting: false
        }));
        yield call(dispatchSaveWriterDeviationFailure, result);
    }
}
function* handleSaveWriterDeviationFailure(action) {
    logger.log('failure', action.payload.error);
}
export default function* saga() {
    yield all([
        takeEvery(saveWriterDeviation, handleSaveWriterDeviation),
        takeEvery(publishWriterDeviation, handlePublishWriterDeviation),
        takeEvery(saveWriterDeviationFailure, handleSaveWriterDeviationFailure)
    ]);
}
